import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { useIntl } from "react-intl";
import { Button, CheckBox, Forms, FormSelect, notify, TFormSelectOption } from "@housebets/housebets-x-package";

import apiUser from "app/api/user";
import { loginOut } from "app/store/slices/user";
import { ISelfExecutionFormProps, TFormikConfig } from "./types";
import { useDispatch } from "react-redux";
import { useLangNavigate } from "app/hooks/useLangNavigate";

export const SelfExecutionForm: React.FC<ISelfExecutionFormProps> = ({
    onSubmit = () => { },
    modifyType,
}) => {
    const langNavigate = useLangNavigate();
    const { $t } = useIntl();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const dispatch = useDispatch();

    const options: TFormSelectOption[] = [
        {
            id: "3",
            name: $t({ id: "MonthA" }, { A: 3 }),
        },
        {
            id: "6",
            name: $t({ id: "MonthA" }, { A: 6 }),
        },
        {
            id: "12",
            name: $t({ id: "YearA" }, { A: 1 }),
        },
        {
            id: "36",
            name: $t({ id: "YearA" }, { A: 3 }),
        },
        {
            id: "60",
            name: $t({ id: "YearA" }, { A: 5 }),
        },
    ];

    const initData: TFormikConfig = {
        target: options[0].id as string,
        agree1: false,
        agree2: false,
        agree3: false,
        agree4: false,
    };

    const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
        formik.setValues({ ...initData });
        formik.setErrors({});
        formik.setTouched({});
        setError?.(false);
    };

    const formSchema = (modify?: boolean) => {
        if (modify) {
            return Yup.object().shape({
                target: Yup.string().required($t({ id: "FieldIsRequired" })),
            });
        }

        return Yup.object().shape({
            target: Yup.string().required($t({ id: "FieldIsRequired" })),
            agree1: Yup.boolean()
                .required($t({ id: "FieldIsRequired" }))
                .oneOf([true], "Error"),
            agree2: Yup.boolean()
                .required($t({ id: "FieldIsRequired" }))
                .oneOf([true], "Error"),
            agree3: Yup.boolean()
                .required($t({ id: "FieldIsRequired" }))
                .oneOf([true], "Error"),
            agree4: Yup.boolean()
                .required($t({ id: "FieldIsRequired" }))
                .oneOf([true], "Error"),
        });
    };

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: { ...initData },
        validationSchema: formSchema(modifyType),
        validateOnBlur: true,
        onSubmit: async (data) => {
            const selPeriod = Number(data.target);
            setIsLoading(true);
            const monthMS = 30 * 24 * 60 * 60 * 1000;
            const time = monthMS * selPeriod;
            try {
                const { status, data } = await apiUser.userSelfExclusion(time);
                if (status === 200 && data.code === 98) {
                    initForm(formik);
                    onSubmit();
                    dispatch(loginOut());
                    setTimeout(() => {
                        navigate(langNavigate("/"));
                    }, 10);
                    return;
                }
                setError($t({ id: "RequestParameterError" }))
                notify({
                    type: "error",
                    title: $t({ id: "RequestParameterError" }),
                });
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        initForm(formik);
    }, []);

    return (
        <Forms.ChangePass.Wrapper>
            <Forms.SelfExclusion.Wrapper>
                <Forms.SelfExclusion.Form
                    onSubmit={formik.handleSubmit}
                >
                    <Forms.SelfExclusion.Row>
                        <FormSelect
                            size="sm"
                            options={options}
                            placeholder={$t({ id: "SelectValue" })}
                            label={$t({ id: "TakeABreakFrom" })}
                            name="target"
                            value={formik.values?.target as string}
                            errors={
                                (!!formik.errors?.target && !!formik.touched?.target) || error
                                    ? {
                                        message: formik.errors?.target || error,
                                    }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("target", val[0])}
                            onFocus={() => {
                                formik.setFieldTouched("target", false);
                            }}
                            onBlur={() => {
                                formik.setFieldTouched("target", true);
                            }}
                        />
                    </Forms.SelfExclusion.Row>

                    {!modifyType && (
                        <Forms.SelfExclusion.Row>
                            <Forms.SelfExclusion.Group>
                                <p>{$t({ id: "ByClickingConfirmIUnderstandThat" })}</p>

                                <CheckBox
                                    id={1}
                                    v2
                                    name="agree1"
                                    className="checkbox"
                                    checked={formik.values?.agree1}
                                    errors={
                                        (!!formik.errors?.agree1 && !!formik.touched?.agree1) || error
                                            ? {
                                                message: formik.errors?.agree1 || error,
                                            }
                                            : null
                                    }
                                    onChange={(val) => formik.setFieldValue("agree1", val)}
                                >
                                    {$t({ id: "SelfExclusionAgree1" })}
                                </CheckBox>

                                <CheckBox
                                    id={2}
                                    v2
                                    name="agree2"
                                    className="checkbox"
                                    checked={formik.values?.agree2}
                                    errors={
                                        (!!formik.errors?.agree2 && !!formik.touched?.agree2) || error
                                            ? {
                                                message: formik.errors?.agree2 || error,
                                            }
                                            : null
                                    }
                                    onChange={(val) => formik.setFieldValue("agree2", val)}
                                >
                                    {$t({ id: "SelfExclusionAgree2" })}
                                </CheckBox>

                                <CheckBox
                                    id={3}
                                    v2
                                    name="agree3"
                                    className="checkbox"
                                    checked={formik.values?.agree3}
                                    errors={
                                        (!!formik.errors?.agree3 && !!formik.touched?.agree3) || error
                                            ? {
                                                message: formik.errors?.agree3 || error,
                                            }
                                            : null
                                    }
                                    onChange={(val) => formik.setFieldValue("agree3", val)}
                                >
                                    {$t({ id: "SelfExclusionAgree3" })}
                                </CheckBox>

                                <CheckBox
                                    id={4}
                                    v2
                                    name="agree4"
                                    className="checkbox"
                                    checked={formik.values?.agree4}
                                    errors={
                                        (!!formik.errors?.agree4 && !!formik.touched?.agree4) || error
                                            ? {
                                                message: formik.errors?.agree4 || error,
                                            }
                                            : null
                                    }
                                    onChange={(val) => formik.setFieldValue("agree4", val)}
                                >
                                    {$t({ id: "SelfExclusionAgree4" })}
                                </CheckBox>
                            </Forms.SelfExclusion.Group>
                        </Forms.SelfExclusion.Row>
                    )}

                    <Forms.SelfExclusion.Row>
                        <Button
                            type="submit"
                            text={modifyType ? $t({ id: "Done" }) : $t({ id: "Confirm" })}
                            themeKind="blue"
                            size="medium-3"
                            disabled={!formik.isValid || isLoading}
                        />
                    </Forms.SelfExclusion.Row>
                </Forms.SelfExclusion.Form>
            </Forms.SelfExclusion.Wrapper>
        </Forms.ChangePass.Wrapper>
    )
}

SelfExecutionForm.displayName = 'SelfExecutionForm'